/* eslint-disable react/jsx-filename-extension */
import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import T from 'i18n-react';
import Link from 'next/link';

import { withViewportProvider } from '@motorway/motorway-component-library';
import { Button } from '@motorway/mw-highway-code';
import { Hyperlink, LoadingIndicator } from '@motorway/mw-highway-code/alpha';

import { logger } from 'Services/logger/logger';
import Content from 'Storybook/local/Content/Content';

import { requestResetPassword } from '../../services/api/user';
import FormBase from '../../utilities/FormBase/FormBase';
import ErrorPage from '../StaticPages/StaticContents/Error/Error';

import styles from './ForgotPassword.module.scss';

T.setTexts(require('../texts.json'));

const VIEWS = Object.freeze({
	CONFIRMED: Symbol('CONFIRMED'),
	ERROR: Symbol('ERROR'),
	FORGOT: Symbol('FORGOT'),
	LOADING: Symbol('LOADING'),
});

class ForgotPassword extends FormBase {
	constructor(props) {
		super(props);

		this.INITIAL_STATE = {
			inputs: [
				{
					focused: false,
					name: 'email',
					showIfInvalid: false,
					valid: true,
					value: '',
				},
			],
			view: VIEWS.LOADING,
		};

		this.state = this.INITIAL_STATE;

		this.$form = React.createRef();
		this.textsPrefix = 'forgotPassword';
	}

	customErrorMessage = (id) => {
		let name = id;

		if (id === 'email' && !this.checkValidity(id) && this.getValue(id)) {
			name = 'email:invalid';
		}

		return name;
	};

	getConfirmedView = () => (
		<div className={styles.confirmedContainer}>
			<h1>{T.translate(`${this.textsPrefix}.confirmedTitle`)}</h1>
			<p>{T.translate(`${this.textsPrefix}.confirmedText`)}</p>
			<p>
				<strong>{this.getValue('email')}</strong>
			</p>
			<Link legacyBehavior passHref as="/" href="/index">
				<Button as="a" icon="chevron" label={T.translate(`${this.textsPrefix}.backHome`)} />
			</Link>
		</div>
	);

	getForgotView = () => (
		<>
			<h1>{T.translate(`${this.textsPrefix}.formTitle`)}</h1>
			<form ref={this.$form} noValidate className={styles.container} onSubmit={this.onSubmit}>
				{this.getTextInput({
					inputProps: {
						pattern: '[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+[.][a-zA-Z]{2,}$',
					},
					label: T.translate(`${this.textsPrefix}.email`),
					name: 'email',
					type: 'email',
				})}
				<div className={styles.spacer} />
				<Button fullWidth label={T.translate(`${this.textsPrefix}.button`)} type="submit" />
				<div className={styles.spacer} />
				<Link legacyBehavior passHref as="/signin" href="/signin">
					<Hyperlink as="a" label={T.translate(`${this.textsPrefix}.back`)} />
				</Link>
			</form>
		</>
	);

	getLoadingView = () => (
		<div className={styles.loading}>
			<LoadingIndicator showText label={T.translate(`${this.textsPrefix}.spinnerText`)} size="large" />
		</div>
	);

	getViewContext = () => {
		const { view } = this.state;

		switch (view) {
			case VIEWS.ERROR:
				return <ErrorPage />;
			case VIEWS.LOADING:
				return this.getLoadingView();
			case VIEWS.CONFIRMED:
				return this.getConfirmedView();
			default:
				return this.getForgotView();
		}
	};

	submit = async () => {
		this.setState({
			view: VIEWS.LOADING,
		});

		try {
			const res = await requestResetPassword({ email: this.getValue('email') });

			this.setState({
				view: res.status === 200 ? VIEWS.CONFIRMED : VIEWS.ERROR,
			});
		} catch (error) {
			this.resetForm({ view: VIEWS.ERROR });

			const message = error?.message || 'There was an error with forgot password form submission';
			logger.error({ error, message, scope: 'ForgotPassword submit' });
		}
	};

	componentDidUpdate = () => {
		if (this.state.view !== VIEWS.FORGOT) {
			window.scrollTo(0, 0);
		}
	};

	componentDidMount = () => {
		let view = VIEWS.FORGOT;

		if (this.props.view === 'loader') {
			view = VIEWS.LOADING;
		}

		if (this.props.view === 'confirmed') {
			view = VIEWS.CONFIRMED;
		}

		if (this.props.view === 'error') {
			view = VIEWS.ERROR;
		}

		this.setState({ view });
	};

	render = () => (
		<div className={styles.root}>
			<Content className={cx(styles.content)}>{this.getViewContext()}</Content>
		</div>
	);
}

ForgotPassword.propTypes = {
	view: PropTypes.oneOf(['loader', 'confirmed']),
};

export default withViewportProvider(ForgotPassword);
